import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { Field } from '../../components/ui'
import { LIST_COLUMNS, LIST_LIMITS, SPEC_SECTIONS, TRANSMISSIONS, TRANSMISSION_KINDS, type FormState, type Kind, type ListKey, type SpecField } from './equipmentForm'

interface ListSpec { key: ListKey; title: string; add: string; remove: string; label: Record<string, string>; max: number }
const LISTS: ListSpec[] = [
  { key: 'bearings', title: 'Rodamientos', add: 'Agregar rodamiento', remove: 'Quitar rodamiento', max: LIST_LIMITS.rows, label: { reference: 'Referencia del rodamiento', fixing_sleeve: 'Manguito de fijación del rodamiento' } },
  { key: 'retainers', title: 'Retenedores', add: 'Agregar retenedor', remove: 'Quitar retenedor', max: LIST_LIMITS.rows, label: { reference: 'Referencia del retenedor' } },
  { key: 'pillow_blocks', title: 'Chumaceras', add: 'Agregar chumacera', remove: 'Quitar chumacera', max: LIST_LIMITS.rows, label: { reference: 'Referencia de la chumacera' } },
  { key: 'parts_list', title: 'Lista de partes', add: 'Agregar parte', remove: 'Quitar parte', max: LIST_LIMITS.parts, label: { position: 'Posición de la parte', description: 'Descripción de la parte', reference: 'Referencia de la parte', quantity: 'Cantidad de la parte', sheet: 'Plano de la parte' } },
]
const PLACEHOLDER: Record<string, string> = { reference: 'Referencia', fixing_sleeve: 'Manguito', position: 'Pos.', description: 'Descripción', quantity: 'Cant.', sheet: 'Plano' }

/** Field keys rendered by this form: API errors on these show next to their input, the rest as a generic alert. */
export const FORM_ERROR_KEYS = new Set(['name', 'notes', 'transmission_type', 'transmission_details', 'motor_has_brake', ...SPEC_SECTIONS.flatMap((s) => s.fields.map((f) => f.key)), ...LISTS.map((l) => l.key)])

const inputMode = (kind: Kind) => (kind === 'integer' ? 'numeric' : kind === 'decimal' ? 'decimal' : undefined)

function Section({ title, hint, open, children }: { title: string; hint?: string; open: boolean; children: ReactNode }) {
  return (
    <details className="spec" open={open}>
      <summary className="spec__summary">
        <span className="spec__text">
          <span className="spec__title">{title}</span>
          {hint ? <span className="spec__hint">{hint}</span> : null}
        </span>
        <svg className="spec__chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
          <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </summary>
      <div className="spec__body">{children}</div>
    </details>
  )
}

interface Props {
  state: FormState
  setState: Dispatch<SetStateAction<FormState>>
  errors: Record<string, string>
  /** Prefix of the input ids, unique per form on the page. */
  idPrefix: string
  /** Sections start expanded (editing) or collapsed (creating: the sheet is optional). */
  defaultOpen: boolean
}

/** The equipment sheet, grouped like the source "FICHA DE INFORMACION": shared by the create and edit forms. */
export function EquipmentSpecFields({ state, setState, errors: fe, idPrefix, defaultOpen }: Props) {
  const setValue = (key: string) => (e: { target: { value: string } }) => setState((s) => ({ ...s, values: { ...s.values, [key]: e.target.value } }))
  const setDetail = (kind: keyof FormState['details'], name: string) => (e: { target: { value: string } }) => setState((s) => ({ ...s, details: { ...s.details, [kind]: { ...s.details[kind], [name]: e.target.value } } }))
  const setCell = (key: ListKey, index: number, column: string, value: string) => setState((s) => ({ ...s, lists: { ...s.lists, [key]: s.lists[key].map((r, i) => (i === index ? { ...r, [column]: value } : r)) } }))
  const addRow = (key: ListKey) => setState((s) => ({ ...s, lists: { ...s.lists, [key]: [...s.lists[key], Object.fromEntries(LIST_COLUMNS[key].map((c) => [c, '']))] } }))
  const removeRow = (key: ListKey, index: number) => setState((s) => ({ ...s, lists: { ...s.lists, [key]: s.lists[key].filter((_, i) => i !== index) } }))

  const scalar = (x: SpecField, noun: string) => {
    const id = `${idPrefix}-${x.key}`
    const label = noun ? `${x.label} (${noun})` : x.label
    let input: ReactNode
    if (x.kind === 'long') input = <textarea id={id} rows={2} value={state.values[x.key]} onChange={setValue(x.key)} />
    else if (x.kind === 'select') input = <select id={id} value={state.values[x.key]} onChange={setValue(x.key)}>{(x.options ?? []).map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>
    else input = <input id={id} value={state.values[x.key]} onChange={setValue(x.key)} inputMode={inputMode(x.kind)} />
    return <Field key={x.key} label={label} htmlFor={id} error={fe[x.key]}>{input}</Field>
  }

  const list = (spec: ListSpec) => (
    <fieldset key={spec.key} className="spec__group">
      <legend className="spec__heading">{spec.title}</legend>
      {state.lists[spec.key].length === 0 ? <p className="muted">Sin registrar</p> : null}
      {state.lists[spec.key].map((row, i) => (
        <div className="row" key={i}>
          {LIST_COLUMNS[spec.key].map((c) => (
            <input key={c} aria-label={`${spec.label[c]} ${i + 1}`} placeholder={PLACEHOLDER[c]} value={row[c]} inputMode={c === 'quantity' ? 'numeric' : undefined} onChange={(e) => setCell(spec.key, i, c, e.target.value)} />
          ))}
          <button type="button" className="btn btn--ghost btn--sm" aria-label={`${spec.remove} ${i + 1}`} onClick={() => removeRow(spec.key, i)}>Quitar</button>
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn btn--ghost btn--sm" disabled={state.lists[spec.key].length >= spec.max} onClick={() => addRow(spec.key)}>{spec.add}</button>
        {state.lists[spec.key].length >= spec.max ? <span className="muted">Máximo {spec.max} filas.</span> : null}
      </div>
      {fe[spec.key] ? <p role="alert" className="form-error">{fe[spec.key]}</p> : null}
    </fieldset>
  )

  return (
    <>
      {SPEC_SECTIONS.map((section) => (
        <Section key={section.title} title={section.title} open={defaultOpen}>
          <div className="spec__grid">{section.fields.map((x) => scalar(x, section.noun))}</div>
          {section.title === 'Motor' ? (
            <label className="check"><input type="checkbox" checked={state.hasBrake} onChange={(e) => setState((s) => ({ ...s, hasBrake: e.target.checked }))} /> Con freno electromagnético</label>
          ) : null}
          {section.title === 'Motor' && fe.motor_has_brake ? <p role="alert" className="form-error">{fe.motor_has_brake}</p> : null}
        </Section>
      ))}
      <Section title="Transmisión" hint="Cadena, correas y acople" open={defaultOpen}>
        <Field label="Tipo de transmisión" htmlFor={`${idPrefix}-transmission_type`} error={fe.transmission_type}>
          <select id={`${idPrefix}-transmission_type`} value={state.values.transmission_type} onChange={setValue('transmission_type')}>
            <option value="">Sin especificar</option>
            {TRANSMISSIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        {TRANSMISSION_KINDS.map((kind) => (
          <section key={kind.key} className="spec__group">
            <h4 className="spec__heading">{kind.title}</h4>
            <div className="spec__grid">
              {kind.fields.map(([name, label]) => (
                <Field key={name} label={`${label} (${kind.noun})`} htmlFor={`${idPrefix}-${kind.key}-${name}`} error={undefined}>
                  <input id={`${idPrefix}-${kind.key}-${name}`} value={state.details[kind.key][name]} onChange={setDetail(kind.key, name)} />
                </Field>
              ))}
            </div>
          </section>
        ))}
        {fe.transmission_details ? <p role="alert" className="form-error">{fe.transmission_details}</p> : null}
      </Section>
      <Section title="Rodamientos y retenedores" hint="Rodamientos, retenedores y chumaceras" open={defaultOpen}>
        {LISTS.filter((l) => l.key !== 'parts_list').map(list)}
      </Section>
      <Section title="Repuestos (plano)" hint="Lista de partes de los planos" open={defaultOpen}>
        {LISTS.filter((l) => l.key === 'parts_list').map(list)}
      </Section>
      <Section title="Observaciones" open={defaultOpen}>
        <Field label="Notas" htmlFor={`${idPrefix}-notes`} error={fe.notes}><textarea id={`${idPrefix}-notes`} rows={3} value={state.values.notes} onChange={setValue('notes')} /></Field>
      </Section>
    </>
  )
}
